import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Link from "@mui/material/Link";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { uberActionCreators } from "../uberModule";
import { errorFieldActionCreators } from "../../../../common/store/modules/errorFieldModule";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import AppModal from "../../../../components/ui/AppModal";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
// helpers
import { isUberVehicleAvailable } from "../helpers/isUberVehicleAvailable";

interface UberSummaryTypes {
  confirmOrderModalVisible: boolean;
  setConfirmOrderModalVisible: (visible: boolean) => void;
  onBookARideBtn: () => void;
  onBuyTicket: () => void;
  selectedFromStation: any;
  selectedToStation: any;
  setNotAvailableMessage: (message: string) => void;
  cancellationPolicy?: string | null;
}

export default function UberSummary({
  confirmOrderModalVisible,
  setConfirmOrderModalVisible,
  onBookARideBtn,
  onBuyTicket,
  selectedFromStation,
  selectedToStation,
  setNotAvailableMessage,
  cancellationPolicy
}: UberSummaryTypes) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [checkPhoneValidation, setCheckPhoneValidation] = useState(false);

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const selectedUberCar = useSelector((state: any) => state.uber.selectedUberCar);
  const dataForUberBooking = useSelector((state: any) => state.uber.dataForUberBooking);
  const uberInitialItem = useSelector((state: any) => state.uber.uberInitialItem);
  const uberVehicle = useSelector((state: any) => state.uber.uberVehicle);
  const errorField = useSelector((state: any) => state.errorField.errorField);
  const uberBookingError = useSelector((state: any) => state.uber.uberBookingError);
  const uberBookingErrorMessage = useSelector((state: any) => state.uber.uberBookingErrorMessage);
  // actions
  const updateUberFormForBooking = useActions(uberActionCreators?.updateUberFormForBookingAction, []);
  const toggleUberEstimationAvailableState = useActions(uberActionCreators?.toggleUberEstimationAvailableState, []);
  const toggleUberBookingError = useActions(uberActionCreators?.toggleUberBookingErrorAction, []);
  const resetErrorField = useActions(errorFieldActionCreators?.resetErrorFieldAction, []);

  useEffect(() => {
    if (uberBookingErrorMessage) {
      const hasPhoneField = uberBookingErrorMessage.some((f: any) => f.field === "phone");
      setCheckPhoneValidation(hasPhoneField);
    }
  }, [uberBookingErrorMessage]);

  useEffect(() => {
    return () => {
      // reset error field on unmount summary component
      resetErrorField();
    };
  }, [resetErrorField]);

  // Handle Forms //

  const handleBookingFormInputChange = (e: any, input: any) => {
    switch (input) {
      case "nameForm":
        updateUberFormForBooking({
          ...dataForUberBooking,
          nameForm: e.target.value
        });
        break;
      case "emailForm":
        updateUberFormForBooking({
          ...dataForUberBooking,
          emailForm: e.target.value
        });
        break;
      case "mobileNumberForm":
        updateUberFormForBooking({
          ...dataForUberBooking,
          mobileNumberForm: e.target.value
        });
        break;
      case "commentForm":
        updateUberFormForBooking({
          ...dataForUberBooking,
          commentForm: e.target.value
        });
        break;
      case "acceptTerms":
        updateUberFormForBooking({
          ...dataForUberBooking,
          acceptTerms: e.target.checked
        });
        break;
      default:
        break;
    }
  };

  // END OF Handle Forms //

  const renderBookErrorMsg = () => {
    // fallback error message, if there are no error messages from API
    if (!uberBookingErrorMessage || uberBookingErrorMessage?.length === 0) {
      return (
        <Box mt={1} className="uber__error-msg">
          {t("transport.prebookCustomError")}
        </Box>
      );
    }

    return uberBookingErrorMessage?.map((error: any) => (
      <Box mt={1} className="uber__error-msg" key={error?.field || Math.random()}>
        {error?.errorMessage ? `${error?.field}: ${error.errorMessage}` : t("transport.prebookCustomError")}
      </Box>
    ));
  };

  const handeEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editRideDetailsText"))) {
      toggleUberEstimationAvailableState();
      setNotAvailableMessage("");
      toggleUberBookingError(false);
    }
  };

  const getSummaryRouteTitle = (): string => {
    return `${t("transport.from")} ${selectedFromStation?.value?.structured_formatting?.main_text} - ${t(
      "transport.to"
    )} ${selectedToStation?.value?.structured_formatting?.main_text}`;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
          <Box>
            <Box className="summary-option-title" mb={2}>
              {t("transport.ticketOption")}
            </Box>
            <Box className="summary-option-title default-text-transform">{getSummaryRouteTitle()}</Box>
          </Box>
          <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
            <AppButton
              label={t("transport.editDetailsBtn")}
              onClick={handeEditTicketOptionButton}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
            />
          </Grid>
        </Box>
        <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2}>
          <Box sx={{ display: "flex", textAlign: "center", alignItems: "center", justifyContent: "center" }}>
            <Grid item xs={3} pl={{ xs: 1, sm: 0 }} className="summary-passenger-box__title">
              {t("transport.invoiceRides")}
            </Grid>
            <Grid item xs={3} className="summary-passenger-box__title">
              Estimated Fare
            </Grid>
            <Grid item xs={3} className="summary-passenger-box__title">
              {t("transport.serviceFee")}
            </Grid>
            <Grid item xs={3} className="summary-passenger-box__title">
              {t("transport.arrivalIn")}
            </Grid>
          </Box>
          <Box sx={{ display: "flex", textAlign: "center", alignItems: "center", justifyContent: "center" }}>
            <Grid item xs={3} pl={{ xs: 1, sm: 0 }}>
              {t("transport.creditCard")}
            </Grid>
            <Grid item xs={3}>
              {selectedUberCar?.price?.uber_price} {selectedUberCar?.price?.currency_code}
            </Grid>
            <Grid item xs={3}>
              {selectedUberCar?.price?.service_fee} {selectedUberCar?.price?.currency_code}
            </Grid>
            <Grid item xs={3}>
              {selectedUberCar?.eta ? `${Math.ceil(selectedUberCar?.eta / 60)} min` : "-"}
            </Grid>
          </Box>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center" }} />
          <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
            {t("transport.totalPrice")}: {selectedUberCar?.price?.total} {selectedUberCar?.price?.currency_code}
          </Box>
        </Box>
      </Box>
      <Grid container width={{ sm: "100%", md: "50%" }} mt={2}>
        {uberInitialItem?.booking_setting?.customer_name !== "hidden" && (
          <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
            <Grid item xs={3}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {t("transport.guestNameLabel")} {uberInitialItem?.booking_setting?.customer_name === "mandatory" && "*"}
              </Box>
            </Grid>
            <Grid item xs={9} ml={1}>
              <AppFormInput
                name={"nameForm"}
                type="string"
                value={dataForUberBooking?.nameForm}
                onChange={event => handleBookingFormInputChange(event, "nameForm")}
                error={errorField?.nameForm}
              />
            </Grid>
          </Grid>
        )}
        {uberInitialItem?.booking_setting?.customer_email !== "hidden" && (
          <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
            <Grid item xs={3}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {t("transport.guestEmailLabel")}{" "}
                {uberInitialItem?.booking_setting?.customer_email === "mandatory" && "*"}
              </Box>
            </Grid>
            <Grid item xs={9} ml={1}>
              <AppFormInput
                name={"emailForm"}
                type="string"
                value={dataForUberBooking?.emailForm}
                onChange={event => handleBookingFormInputChange(event, "emailForm")}
                error={errorField?.emailForm}
              />
            </Grid>
          </Grid>
        )}
        {uberInitialItem?.booking_setting?.customer_phone !== "hidden" && (
          <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
            <Grid item xs={3}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {t("transport.guestPhoneLabel")}{" "}
                {uberInitialItem?.booking_setting?.customer_phone === "mandatory" && "*"}
              </Box>
            </Grid>
            <Grid item xs={9} ml={1}>
              <AppFormInput
                name={"mobileNumberForm"}
                type="string"
                value={dataForUberBooking?.mobileNumberForm}
                onChange={event => handleBookingFormInputChange(event, "mobileNumberForm")}
                error={errorField?.mobileNumberForm || checkPhoneValidation}
              />
              <Box sx={{ fontSize: { xs: 10, sm: 12 }, mt: 1 }}>{t("phoneNumberLabel")}</Box>
            </Grid>
          </Grid>
        )}
        {uberInitialItem?.booking_setting?.comment !== "hidden" && (
          <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
            <Grid item xs={3}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {t("transport.commentLabel")} {uberInitialItem?.booking_setting?.comment === "mandatory" && "*"}
              </Box>
            </Grid>
            <Grid item xs={9} ml={1}>
              <AppFormInput
                name={"commentForm"}
                type="string"
                value={dataForUberBooking?.commentForm}
                onChange={event => handleBookingFormInputChange(event, "commentForm")}
                error={errorField?.commentForm}
              />
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
          <Grid item style={{ display: "flex", alignItems: "center" }}>
            <Checkbox
              sx={{
                "&&.PrivateSwitchBase-root": {
                  paddingLeft: 0
                }
              }}
              checked={dataForUberBooking?.acceptTerms}
              onChange={event => handleBookingFormInputChange(event, "acceptTerms")}
            />
            <Box style={{ display: "inline-flex", flexDirection: "column" }}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {t("transport.termsCheckboxForUber")}
                <Link
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  sx={{ fontSize: { xs: 12, sm: 14 }, color: "#76a8cf", marginLeft: 1, textDecoration: "underline" }}
                  href={uberInitialItem?.additional_conditions}
                >
                  {t("viewLabel")}
                </Link>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <AppModal
          title={uberInitialItem?.uber_settings?.additional_terms_and_conditions_header}
          isOpen={confirmOrderModalVisible}
          handleOpenModal={() => {
            setConfirmOrderModalVisible(true);
          }}
          handleCloseModal={() => {
            setConfirmOrderModalVisible(false);
          }}
        >
          <Grid>
            <Box onClick={() => {}} className="provider-box" data-cy="provider-box">
              {uberInitialItem?.uber_settings?.additional_terms_and_conditions_image_url && (
                <img
                  className="provider-image"
                  alt="provider-img"
                  src={uberInitialItem?.uber_settings?.additional_terms_and_conditions_image_url}
                />
              )}
            </Box>
            <Box mt={2} mb={2}>
              {uberInitialItem?.uber_settings?.additional_terms_and_conditions_text}
            </Box>

            <Grid item style={{ display: "flex", alignItems: "center" }} mt={1}>
              <Typography sx={{ fontSize: { xs: 12, sm: 14 } }}>
                {uberInitialItem?.uber_settings?.additional_terms_and_conditions_label}
                <Link
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  sx={{ fontSize: { xs: 12, sm: 14 }, color: "#76a8cf", marginLeft: 1, textDecoration: "underline" }}
                  href={uberInitialItem?.uber_settings?.additional_terms_and_conditions_url}
                >
                  {t("viewLabel")}
                </Link>
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ width: { xs: "100%", sm: "100%", md: "100%" }, margin: "25px auto 0 auto" }}>
              <AppButton
                label={t("transport.confirmOrder")}
                onClick={onBuyTicket}
                disableRipple={true}
                customBackgroundColor={tenantInfo?.style?.button?.color}
                customActiveColor={tenantInfo?.style?.button?.active}
                customHoverColor={tenantInfo?.style?.button?.hover}
                customClickColor={tenantInfo?.style?.button?.click}
                type="submit"
                fontSize="14px"
              />
            </Grid>
          </Grid>
        </AppModal>
      </Grid>
      {errorField?.acceptTerms && (
        <Box mt={1} className="uber__error-msg">
          {t("transport.termsError")}
        </Box>
      )}
      {uberBookingError && renderBookErrorMsg()}

      {cancellationPolicy && (
        <Box pt={6}>
          <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
        </Box>
      )}

      <Grid
        item
        xs={12}
        sx={{
          width: { xs: "100%", sm: "100%", md: "30%" },
          margin: cancellationPolicy?.trim() ? "24px auto 0 auto" : "25px auto 0 auto"
        }}
      >
        <AppButton
          label={t("transport.bookButton")}
          onClick={onBookARideBtn}
          disableRipple={true}
          customBackgroundColor={tenantInfo?.style?.button?.color}
          customActiveColor={tenantInfo?.style?.button?.active}
          customHoverColor={tenantInfo?.style?.button?.hover}
          customClickColor={tenantInfo?.style?.button?.click}
          fontSize="14px"
          disabled={!isUberVehicleAvailable(uberVehicle)}
        />
      </Grid>
    </Box>
  );
}
